import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Camera,
  PencilSimple,
  Trash,
  Printer,
  FilePdf,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/AppHeader";
import { SlabTracer } from "@/components/SlabTracer";
import { RemnantLabel } from "@/components/RemnantLabel";
import { PhotoGallery } from "@/components/PhotoGallery";
import { PhotoEditor } from "@/components/PhotoEditor";
import { ShapeCropDialog } from "@/components/ShapeCropDialog";
import { hasPendingHd, usePendingHdRefresh } from "@/lib/photoHd";
import { api } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";
import { useDirectory } from "@/lib/directory";

const STATUSES = ["Available", "Reserved", "Used"];
const STATUS_BADGE = {
  Available: "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
  Reserved: "border-amber-500/25 bg-amber-500/10 text-amber-400",
  Used: "border-zinc-500/25 bg-zinc-500/10 text-zinc-400",
};

export default function RemnantDetail() {
  const { id } = useParams();
  const { nameOf } = useDirectory();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null);
  const [cropping, setCropping] = useState(null); // photo being cut to the traced shape
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceDraft, setPriceDraft] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  const fileRef = useRef(null);
  const labelRef = useRef(null);

  const setStatus = async (status) => {
    if (status === r.status) return;
    try {
      const { data } = await api.patch(`/remnants/${id}/status`, { status });
      setR(data);
      toast.success(`${data.code} → ${status}`);
    } catch {
      toast.error("Could not change status");
    }
  };

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      const { data } = await api.patch(`/remnants/${id}/pricing`, {
        sale_price_per_sqft: priceDraft === "" ? null : Number(priceDraft),
      });
      setR(data);
      setEditingPrice(false);
      toast.success("Sale price saved");
    } catch {
      toast.error("Could not save sale price");
    }
    setSavingPrice(false);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/remnants/${id}/notes`, {
        notes: notesDraft,
      });
      setR(data);
      setEditingNotes(false);
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    }
    setSavingNotes(false);
  };

  const downloadPdf = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);
    const canvas = await html2canvas(labelRef.current, {
      scale: 3,
      backgroundColor: "#ffffff",
    });
    const pdf = new jsPDF({
      unit: "in",
      format: [4, 6],
      orientation: "portrait",
    });
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, 4, 6);
    pdf.save(`${r.code}-label.pdf`);
    toast.success("Label PDF downloaded");
  };

  useEffect(() => {
    api
      .get(`/remnants/${id}`)
      .then(({ data }) => setR(data))
      .catch(() => toast.error("Remnant not found"));
  }, [id]);

  const refreshRemnant = useCallback(
    () =>
      api
        .get(`/remnants/${id}`)
        .then(({ data }) => setR(data))
        .catch(() => {}),
    [id],
  );
  usePendingHdRefresh(hasPendingHd(r?.photos), refreshRemnant);

  // photoId set means we are swapping an existing photo, not adding a new one
  const savePhoto = async (file, photoId) => {
    setUploading(true);
    file = await shrinkForUpload(file);
    const fd = new FormData();
    fd.append("file", file);
    if (file.enhanced) fd.append("enhanced", "true");
    try {
      const { data } = await api.post(
        photoId
          ? `/remnants/${id}/photos/${photoId}`
          : `/remnants/${id}/photos`,
        fd,
      );
      setR(data);
      toast.success(
        photoId
          ? "Photo updated"
          : hasPendingHd(data.photos)
            ? "Photo attached — HD version on its way"
            : "Photo attached",
      );
    } catch (err) {
      toast.error("Photo upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const revertHd = async (photo) => {
    try {
      const { data } = await api.post("/photos/revert-hd", {
        collection: "remnants",
        doc_id: id,
        photo_id: photo.id,
      });
      setR(data);
      toast.success("Original photo restored");
    } catch {
      toast.error("Could not restore the original");
    }
  };

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (file) setPending({ file, photoId: null });
    e.target.value = "";
  };

  const editPhoto = async (p) => {
    try {
      const { data } = await api.get(`/photos/${p.storage_path}`, {
        responseType: "blob",
      });
      setPending({
        photoId: p.id,
        file: new File([data], p.original_filename || "photo.jpg", {
          type: data.type || "image/jpeg",
        }),
      });
    } catch {
      toast.error("Could not open that photo");
    }
  };

  const removePhoto = async (photoId) => {
    const { data } = await api.delete(`/remnants/${id}/photos/${photoId}`);
    setR(data);
    toast.success("Photo removed");
  };

  const cutoutPhoto = async (photo) => {
    try {
      const { data } = await api.post(photo.cutout ? "/photos/cutout/undo" : "/photos/cutout", {
        collection: "remnants",
        doc_id: id,
        photo_id: photo.id,
      });
      setR(data);
      toast.success(photo.cutout ? "Original restored" : "Background removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not edit the photo");
    }
  };

  // transparent PNG of just the usable shape — kept beside the original
  const saveShapePhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("shape", "true");
    try {
      const { data } = await api.post(`/remnants/${id}/photos`, fd);
      setR(data);
      setCropping(null);
      toast.success("Shape photo saved — it now shows up in the slab layout picker");
    } catch {
      toast.error("Could not save the cropped photo");
    }
  };

  const remove = async () => {
    await api.delete(`/remnants/${id}`);
    toast.success("Remnant deleted");
    navigate("/inventory/remnants");
  };

  if (!r)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Remnant" />
        <div className="mx-auto max-w-3xl px-4 pt-10">
          <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
        </div>
      </div>
    );

  const photos = (r.photos || []).filter((p) => !p.is_deleted);
  const rows = [
    ...(r.material || r.finish
      ? [["Material", [r.material, r.finish].filter(Boolean).join(" · ")]]
      : []),
    ...(r.brand ? [["Brand", r.brand]] : []),
    ...(r.supplier ? [["Supplier", r.supplier]] : []),
    ["Thickness", r.thickness],
    ["Usable Sq Ft", `${r.usable_sqft} ft²`],
    ["Allocated", `${r.allocated_sqft || 0} ft²`],
    [
      "Remaining",
      `${Math.round(((r.usable_sqft || 0) - (r.allocated_sqft || 0)) * 10) / 10} ft²`,
    ],
    ["Rack", r.location || "—"],
    ["Logged By", nameOf(r.created_by) || "—"],
  ];

  return (
    <div className="grain min-h-screen">
      <AppHeader title={r.code} />
      <main className="relative z-10 mx-auto max-w-5xl px-4 pb-24 pt-6">
        <button
          onClick={() => navigate("/inventory/remnants")}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> All remnants
        </button>

        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="remnant-header-card"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-mono-data text-xs uppercase tracking-widest text-zinc-500">
                {r.code}
              </div>
              <h1
                className="text-2xl font-bold tracking-tight sm:text-3xl"
                data-testid="remnant-detail-color"
              >
                {r.slab_color}
                <span
                  data-testid="remnant-status-badge"
                  className={`ml-3 inline-block rounded-full border px-2.5 py-0.5 align-middle text-[10px] font-semibold uppercase leading-tight tracking-widest ${
                    STATUS_BADGE[r.status] || STATUS_BADGE.Used
                  }`}
                >
                  {r.status}
                </span>
              </h1>
              <div
                className="mt-3 flex flex-wrap gap-2"
                data-testid="remnant-status-quick-edit"
              >
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`remnant-set-status-${s.toLowerCase()}`}
                    onClick={() => setStatus(s)}
                    className={`rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors duration-150 ${
                      r.status === s
                        ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-300"
                        : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {r.source_slab_id ? (
                <p className="mt-1 text-sm text-zinc-400">
                  Cut from slab{" "}
                  <Link
                    to={`/inventory/slabs/${r.source_slab_id}`}
                    data-testid="remnant-source-slab-link"
                    className="text-yellow-400 hover:underline"
                  >
                    {r.source_slab_code || r.source_tag}
                  </Link>
                </p>
              ) : (
                r.source_tag && (
                  <p className="mt-1 text-sm text-zinc-400">
                    From{" "}
                    <Link
                      to={`/reservation/${r.source_reservation_id}`}
                      data-testid="remnant-source-link"
                      className="text-yellow-400 hover:underline"
                    >
                      {r.source_tag}
                    </Link>{" "}
                    · {r.customer_name}
                  </p>
                )
              )}
            </div>
            <div
              className="font-mono-data text-3xl font-bold text-yellow-400"
              data-testid="remnant-detail-sqft"
            >
              {r.usable_sqft} ft²
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <SlabTracer
              points={r.polygon || []}
              onChange={() => {}}
              lengthIn={r.length_in}
              widthIn={r.width_in}
              readOnly
            />
            <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-950 p-4">
              <div className="mb-3 text-xs uppercase tracking-widest text-zinc-500">
                Label Preview — 4 x 6 in
              </div>
              <div
                id="print-area"
                className="origin-top-left scale-[0.72] sm:scale-90"
                style={{ width: "4in" }}
              >
                <RemnantLabel r={r} forwardRef={labelRef} />
              </div>
            </div>
          </div>

          <div>
            <div className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-900">
              {rows.map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <span className="text-xs uppercase tracking-widest text-zinc-500">
                    {k}
                  </span>
                  <span
                    data-testid={`remnant-detail-${k.toLowerCase().replace(/ /g, "-")}`}
                    className="font-mono-data text-right text-sm font-medium"
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>

            <div
              className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="remnant-pricing-panel"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Sale Price
                </div>
                {!editingPrice && (
                  <button
                    data-testid="remnant-edit-pricing-button"
                    onClick={() => {
                      setPriceDraft(r.sale_price_per_sqft ?? "");
                      setEditingPrice(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editingPrice ? (
                <div className="mt-3 space-y-3">
                  <div className="space-y-1">
                    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                      Sale / Sq Ft
                    </span>
                    <Input
                      data-testid="remnant-sale-input"
                      type="number"
                      step="0.01"
                      min="0"
                      autoFocus
                      value={priceDraft}
                      onChange={(e) => setPriceDraft(e.target.value)}
                      placeholder="—"
                      className="h-11 border-zinc-800 bg-zinc-950"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="remnant-save-pricing-button"
                      disabled={savingPrice}
                      onClick={savePricing}
                      className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      {savingPrice ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      data-testid="remnant-cancel-pricing-button"
                      variant="outline"
                      onClick={() => setEditingPrice(false)}
                      className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 divide-y divide-zinc-800 rounded-md border border-zinc-800 bg-zinc-950">
                  {[
                    [
                      "Sale / Sq Ft",
                      r.sale_price_per_sqft != null
                        ? `$${r.sale_price_per_sqft}`
                        : "—",
                    ],
                    [
                      "Remnant Value",
                      r.sale_price_per_sqft != null
                        ? `$${Math.round(r.sale_price_per_sqft * (r.usable_sqft || 0) * 100) / 100}`
                        : "—",
                    ],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between gap-4 px-3 py-2.5"
                    >
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {k}
                      </span>
                      <span
                        data-testid={`remnant-detail-${k.toLowerCase().replace(/[ /]+/g, "-")}`}
                        className="font-mono-data text-right text-sm font-medium"
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Allocated To Jobs
              </div>
              <div className="mt-3 space-y-2" data-testid="remnant-allocations">
                {(r.allocations || []).length === 0 && (
                  <p className="text-sm text-zinc-500">
                    Not pulled for any job yet.
                  </p>
                )}
                {(r.allocations || []).map((a) => (
                  <Link
                    key={a.id}
                    to={`/reservation/${a.reservation_id}`}
                    data-testid={`remnant-allocation-${a.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 p-3 transition-colors duration-150 hover:border-zinc-700 hover:bg-zinc-800"
                  >
                    <div className="min-w-0">
                      <div className="font-mono-data text-xs text-yellow-500">
                        {a.tag}
                      </div>
                      <div className="truncate text-sm">{a.customer_name}</div>
                    </div>
                    <span className="font-mono-data text-sm font-bold">
                      {a.sqft} ft²
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Notes
                </div>
                {!editingNotes && (
                  <button
                    data-testid="remnant-edit-notes-button"
                    onClick={() => {
                      setNotesDraft(r.notes || "");
                      setEditingNotes(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editingNotes ? (
                <div className="mt-3">
                  <Textarea
                    data-testid="remnant-notes-input"
                    autoFocus
                    rows={4}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    className="border-zinc-800 bg-zinc-950"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      data-testid="remnant-save-notes-button"
                      disabled={savingNotes}
                      onClick={saveNotes}
                      className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      {savingNotes ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      data-testid="remnant-cancel-notes-button"
                      variant="outline"
                      onClick={() => setEditingNotes(false)}
                      className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p
                  className="mt-2 whitespace-pre-wrap text-sm text-zinc-200"
                  data-testid="remnant-detail-notes"
                >
                  {r.notes || "—"}
                </p>
              )}
            </div>

            <div className="no-print mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">
                    Photos
                  </div>
                  <p className="mt-1 text-xs text-zinc-500">
                    Crop it and drop the yard out of the background before it
                    goes on the website. <span className="text-amber-300/80">Shape</span> cuts a photo to the traced usable field.
                  </p>
                </div>
                <Button
                  data-testid="remnant-add-photo-button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="h-10 shrink-0 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-wider hover:bg-zinc-800"
                >
                  <Camera size={16} weight="bold" className="mr-2" />
                  {uploading ? "Uploading…" : "Add Photo"}
                </Button>
                <input
                  ref={fileRef}
                  data-testid="remnant-photo-file-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={pickPhoto}
                  className="hidden"
                />
              </div>
              <PhotoGallery
                photos={photos}
                onRemove={removePhoto}
                onEdit={editPhoto}
                onCropShape={(r.polygon || []).length >= 3 ? (p) => setCropping(p) : undefined}
                onRevertHd={revertHd}
                onCutout={cutoutPhoto}
                testPrefix="remnant-photo"
              />
              {cropping && (
                <ShapeCropDialog
                  photo={cropping}
                  polygon={r.polygon || []}
                  lengthIn={r.length_in}
                  widthIn={r.width_in}
                  code={r.code}
                  onDone={saveShapePhoto}
                  onClose={() => setCropping(null)}
                />
              )}
              {pending && (
                <PhotoEditor
                  file={pending.file}
                  onDone={(f) => {
                    const target = pending.photoId;
                    setPending(null);
                    savePhoto(f, target);
                  }}
                  onClose={() => setPending(null)}
                />
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                data-testid="print-remnant-label-button"
                onClick={() => window.print()}
                className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <Printer size={18} weight="bold" className="mr-2" /> Print 4x6
                Label
              </Button>
              <Button
                data-testid="remnant-pdf-button"
                variant="outline"
                onClick={downloadPdf}
                className="h-12 border-zinc-700 bg-transparent font-semibold uppercase tracking-wider hover:bg-zinc-800"
              >
                <FilePdf size={18} weight="bold" className="mr-2" /> PDF
              </Button>
              <Button
                data-testid="edit-remnant-button"
                onClick={() => navigate(`/inventory/remnants/${id}/edit`)}
                variant="outline"
                className="h-12 border-zinc-700 bg-transparent font-semibold uppercase tracking-wider hover:bg-zinc-800"
              >
                <PencilSimple size={18} weight="bold" className="mr-2" /> Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="delete-remnant-button"
                    variant="outline"
                    className="h-12 border-red-900/60 bg-transparent font-semibold uppercase tracking-wider text-red-400 hover:bg-red-950/40"
                  >
                    <Trash size={18} weight="bold" className="mr-2" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-zinc-800 bg-zinc-900">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {r.code}?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      This removes the remnant from inventory.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel
                      data-testid="cancel-remnant-delete"
                      className="border-zinc-700"
                    >
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="confirm-remnant-delete"
                      onClick={remove}
                      className="bg-red-600 text-white hover:bg-red-500"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
